import Axios from 'axios'
import classnames from 'classnames'
import { Component, createRef } from 'preact'
import Portal from 'preact-portal'
import Tooltip from '../../control-components/ui-components/Tooltip'
import Phrase from '../../libs/translationsUtils/phrase'
import { common } from '../common'
import Content from '../common/Content'
import PostActions from '../content/post-actions.sync'
import Editor from '../editor'
import PinnedReplyLabel from '../pinned-reply/PinnedReplyLabel'
import { Person } from '../product-updates-destination/Types'
import { LikedUserType } from '../topic-list-view/LikedUsers'
import TopicPublicTags from '../topic-list-view/TopicPublicTags'
import TopicVote from '../topic-list-view/TopicVote'
import { PostType } from '../topic-list-view/Types'
import VotedUsers from '../topic-list-view/VotedUsers'
import UserAvatar from '../User/UserAvatar'
import { CONTENT_TYPE_IDEA } from '@insided/shared/utils/contentType'
import { canUserEditPost } from '../utils/permissions'
import formatDateInLocalTimezone from '../utils/dateFormatDestination'
import { PageUnloadContext } from '@insided/shared/services/PageUnloadContext'

interface IdeaType {
  submittedAt: string
  contentType: string
  id: string
  title: string
  content: string
  tags: string[]
  votedBy: string[]
  author: Person
  publicId: string
}

export interface IdeationTopicViewProps {
  topic: IdeaType
  currentUser: LikedUserType
  pinnedReply: PostType | null
  isSubscribed: boolean
  currentPage: number
}

export interface IdeationTopicViewState {
  content: string | null
  tags: string[]
  isCurrentUserVoted: boolean
  votedUsers: {
    [key: string]: LikedUserType
  }
  showEditor: boolean
  isLoading: boolean
  editorErrors: Array<{
    category: string
    phrase: string
  }>
  isSubscribed: boolean
  hideFirstPost: boolean
}

class IdeationTopicView extends Component<IdeationTopicViewProps, IdeationTopicViewState> {
  private readonly canUserEditPost: boolean = canUserEditPost()
  private readonly isProductAreasEnabled: boolean = common.isModuleActive('productAreas')

  constructor(props: IdeationTopicViewProps) {
    super(props)
    this.state = {
      content: props.topic.content,
      isCurrentUserVoted:
        this.props.currentUser.id !== null &&
        this.props.topic.votedBy.includes(this.props.currentUser.id.toString()),
      votedUsers: {},
      showEditor: false,
      isLoading: false,
      editorErrors: [],
      tags: props.topic.tags,
      isSubscribed: props.isSubscribed,
      hideFirstPost: props.currentPage > 1
    }
    this.handleVoteStatusToggle = this.handleVoteStatusToggle.bind(this)
    this.handleOpeningPostChange = this.handleOpeningPostChange.bind(this)
    this.handleSubscriptionToggle = this.handleSubscriptionToggle.bind(this)
    this.onChange = this.onChange.bind(this)
    this.onCancel = this.onCancel.bind(this)
  }

  async handleSubscriptionToggle() {
    if (!this.props.currentUser) return

    if (this.props.currentUser.id === null) {
      const event = new CustomEvent('GuestUserAction')
      window.dispatchEvent(event)
      return
    }

    const endpoint = `/ideas/${this.props.topic.id}/${
      this.state.isSubscribed ? 'unsubscribe' : 'subscribe'
    }`
    await Axios.post(endpoint)

    this.setState({ isSubscribed: !this.state.isSubscribed })
  }

  content = createRef()
  // This content ref is responsible for storing the previous value of the post when we edit topic.
  // onChange method keeps updating the state and when we cancel the edit the content uses the value from state
  // which is changed. We can also set the value we receive from props but this is received from twig, and it is
  // not refreshed until we refresh the browser.

  async handleVoteStatusToggle() {
    if (this.props.currentUser.id === null) {
      const event = new CustomEvent('GuestUserAction')
      window.dispatchEvent(event)
      return
    }

    if (this.state.isCurrentUserVoted) {
      delete this.state.votedUsers[this.props.currentUser.id]
      this.setState({ votedUsers: this.state.votedUsers, isCurrentUserVoted: false, isSubscribed: false })
      await Axios.post(`/community/api/ideas/${this.props.topic.id}/unvote`, {
        unvotedBy: this.props.currentUser.id
      })
    } else {
      this.setState({
        votedUsers: {
          ...this.state.votedUsers,
          [this.props.currentUser.id]: this.props.currentUser
        },
        isCurrentUserVoted: true,
        isSubscribed: true
      })
      await Axios.post(`/community/api/ideas/${this.props.topic.id}/vote`, {
        votedBy: this.props.currentUser.id
      })
    }
  }

  async handleOpeningPostChange() {
    this.setState({ isLoading: true })
    try {
      await Axios.post(`/community/api/ideas/${this.props.topic.id}/editContent`, {
        content: this.state.content
      })
      this.setState(state => {
        this.content.current = state.content
        return { showEditor: false, content: state.content, isLoading: false }
      })
    } catch (e) {
      this.setState({ isLoading: false })
      if (e.response?.data?.errors?.content) {
        this.setState({ editorErrors: [{ category: 'Forum', phrase: 'validation.text.required' }] })
      } else {
        this.setState({ editorErrors: [{ category: 'Forum', phrase: "Something's gone wrong" }] })
      }
    }
  }

  async componentDidMount() {
    if (this.props.topic.votedBy.length) {
      const res = await Axios.post('/member/getUsersById', {
        ids: this.props.topic.votedBy
      })

      this.setState({ votedUsers: res.data })
    }
    this.content.current = this.props.topic.content
  }

  onChange (content: string) {
    this.setState({ content })
  }

  onCancel () {
    this.setState({ showEditor: false, content: this.content.current })
  }

  render() {
    const { topic, pinnedReply, currentPage } = this.props
    const {
      content,
      isCurrentUserVoted,
      votedUsers,
      showEditor,
      isLoading,
      editorErrors,
      tags,
      hideFirstPost
    } = this.state
    const { author, submittedAt, content: data } = topic
    const showReport = common.isModuleActive('reportedContent')

    return (
      <div>
        <div
          className={classnames(
            'post',
            'box__pad',
            'qa-topic-post-box',
            'post--odd',
            'js-toggle-target',
            `${hideFirstPost ? 'is-hidden' : ''}`
          )}
        >
          <Portal into='.qa-topic-meta'>
            <li className='list-item--publish-date'>
              <Tooltip
                withArrow
                trigger={<time> {common.relativeDate(submittedAt)} </time>}
                tooltipMessage={
                  <div
                    className={classnames(
                      'tooltip__content',
                      'tooltip__content--publish-date',
                      'js-tooltip-content',
                      'position--bottom'
                    )}
                  >
                    {formatDateInLocalTimezone(new Date(submittedAt))}
                  </div>
                }
              />
            </li>
          </Portal>
          <Portal into='.ideation-topic-votes'>
            <div className='ideation-topic-votes-wrapper'>
              <TopicVote
                displayText
                currentUserVoted={isCurrentUserVoted}
                numberOfVotes={Object.keys(votedUsers).length}
                onClick={this.handleVoteStatusToggle}
              />
              {Boolean(Object.keys(votedUsers).length) && (
                <VotedUsers
                  votedUsers={Object.keys(votedUsers).map((id) => votedUsers[id])}
                  previewCount={5}
                  isCurrentUserVoted={isCurrentUserVoted}
                />
              )}
            </div>
          </Portal>
          <div className='post__user__info'>
            <UserAvatar
              user={{ ...author, name: author.username }}
              extraClass='avatar avatar--responsive'
              showRank
            />
          </div>
          <div className='post__user__meta'>
            <ul className={classnames('list', 'list--dot-seperated', 'text--meta')}>
              <li>
                <div className='group--inline'>
                  <a href={`/members/user-${author.id}`}>{author.username}</a>
                </div>
              </li>
              <li className='is-hidden-S'>
                <Phrase
                  p='topic.meta.post.reply'
                  count={{ n: author.reputation.repliesMade.toString() }}
                />
              </li>
            </ul>
          </div>
          <div>
            {showEditor
              ? (
                <PageUnloadContext.Consumer>
                  {({ lock, unlock }) => {
                    return (
                      <div className='post__content post__edit'>
                        <Editor
                          data={this.state.content}
                          attachments={[]}
                          canHavePoll={false}
                          isLoading={isLoading}
                          errors={editorErrors}
                          onSubmit={async () => {
                            unlock()
                            await this.handleOpeningPostChange()
                          }}
                          onCancel={() => {
                            unlock()
                            this.onCancel()
                          }}
                          onChange={(content: string) => {
                            lock(content, data)
                            this.onChange(content)
                          }}
                          profile='openingPost'
                        />
                      </div>
                    )
                  }}
                </PageUnloadContext.Consumer>
              )
              : (
                <div>
                  <Content
                    content={content ?? ''}
                    className={classnames(
                      'post__content',
                      'qa-topic-post-content',
                      'post__content--new-editor'
                    )}
                  />
                  {pinnedReply && <PinnedReplyInContent pinnedReply={pinnedReply} />}
                </div>
              )}
          </div>
          {this.isProductAreasEnabled && (
            <TopicPublicTags
              topicId={topic.id}
              contentType={CONTENT_TYPE_IDEA}
              tags={tags}
              canUserEdit={this.canUserEditPost}
              onChange={(newTags) => this.setState({ tags: newTags })}
            />
          )}

          <div className='post__footer'>
            <PostActions
              privateId={parseInt(topic.id)}
              topicId={parseInt(topic.publicId)}
              topicTitle={topic.title}
              contentType={CONTENT_TYPE_IDEA}
              onEditClick={() => this.setState({ showEditor: true })}
              showLike={false}
              showReport={showReport}
              showBestAnswer={false}
              quoteText={topic.content}
              quoteUsername={topic.author.username}
              showEdit={this.canUserEditPost}
              isSubscribed={this.state.isSubscribed}
            />
          </div>
          {currentPage > 1 && (
            <a
              href='#'
              className='first-post__close'
              onClick={() => this.setState({ hideFirstPost: !hideFirstPost })}
            >
              {hideFirstPost
                ? (
                  <Phrase p='showFirstpost.message' />
                )
                : (
                  <Phrase p='hideFirstpost.message' />
                )}
            </a>
          )}
        </div>
      </div>
    )
  }
}

const PinnedReplyInContent = ({ pinnedReply }: { pinnedReply: PostType }) => (
  <div className='post__content pinned-reply__label--firstpost'>
    <PinnedReplyLabel phrase={<Phrase p='reply.pinned' m='Forum' />} />
    <div className='post--pinned pinned-reply-field'>
      <p className='pinned-reply-field__header'>
        <div className='pinned-reply-field__user-info'>
          <UserAvatar
            user={{ ...pinnedReply.author, ...{ name: pinnedReply.author.username } }}
            extraClass='avatar avatar--responsive avatar--pinned-reply'
            showRank
          />
          {String(pinnedReply.author.id) === ''
            ? (
              <span className='pinned-reply__username'> {pinnedReply.author.username} </span>
            )
            : (
              <a href={`/members/user-${pinnedReply.author.id}`}>{pinnedReply.author.username}</a>
            )}
        </div>
        <span className='text--meta'>
          <Tooltip
            withArrow
            trigger={<time> {common.relativeDate(pinnedReply.repliedAt)} </time>}
            tooltipMessage={
              <div
                className={classnames(
                  'tooltip__content',
                  'tooltip__content--publish-date',
                  'js-tooltip-content',
                  'position--bottom'
                )}
              >
                {formatDateInLocalTimezone(new Date(pinnedReply.repliedAt))}
              </div>
            }
          />
        </span>
      </p>
      <Content content={pinnedReply.content} className='pinned-reply__content' />
    </div>
  </div>
)

export default IdeationTopicView
